import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers } from '@/lib/utils/queries'
import {
  extractCustomZodIssues,
  extractZodIssues,
  getFirstZodErrorMessage,
  getTranslatedCustomIssueDescription,
} from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { UpdateFederalExciseMappings } from '@repo/backend/crt'
import { MarkingCode, PRODUCT_CATEGORIES, ProductCategory } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// The grid's product category lives in the URL so the data-issues dialog can deep-link to a category.
export const federalExciseSearchSchema = z.object({
  productCategory: z.enum(PRODUCT_CATEGORIES).optional(),
})

export type FederalExciseSearch = z.infer<typeof federalExciseSearchSchema>

export type FederalExciseMappingsList = Awaited<ReturnType<typeof getFederalExciseMappings>>
export type FederalExciseMappingRow = FederalExciseMappingsList['results'][number]

/** A mapping row flattened to the marking grid's shape: the key's name as the row label, one column per marking. */
export type FederalExciseGridRow = {
  productCategory: ProductCategory
  key: string
  productType: string
} & { [K in MarkingCode]: string | null }

export function toFederalExciseGridRow(row: FederalExciseMappingRow): FederalExciseGridRow {
  return {
    productCategory: row.productCategory,
    key: row.key,
    productType: row.name,
    ...row.values,
  }
}

async function getFederalExciseMappings(productCategory: ProductCategory, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['federal-excise'].$get(
    { query: { productCategory } },
    { init: { signal } },
  )
  return await res.json()
}

async function getFederalExciseLastUpdated(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['federal-excise']['last-updated'].$get({}, { init: { signal } })
  return await res.json()
}

async function getUnmappedFederalExciseCount(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['federal-excise']['unmapped-count'].$get({}, { init: { signal } })
  return await res.json()
}

async function updateFederalExciseMappings(data: UpdateFederalExciseMappings) {
  const api = getApi()
  const res = await api.crt['federal-excise'].$patch({ json: data })
  return await res.json()
}

async function getFederalExciseCsv(signal?: AbortSignal) {
  return await fetchCsvExport('/crt/federal-excise/export', signal)
}

export const federalExciseExportQueryOptions = () => ({
  queryKey: ['federalExcise', 'export'],
  queryFn: ({ signal }: { signal: AbortSignal }) => getFederalExciseCsv(signal),
})

/** How many grid cells still have no amount entered, for the header data-issues dialog. */
export const unmappedFederalExciseCountQueryOptions = {
  queryKey: ['federalExcise', 'unmapped-count'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getUnmappedFederalExciseCount(signal),
}

export function useFederalExciseMappings(productCategory: ProductCategory) {
  return useQuery({
    queryKey: ['federalExcise', 'mappings', productCategory],
    queryFn: ({ signal }) => getFederalExciseMappings(productCategory, signal),
    refetchOnWindowFocus: false,
  })
}

export function useFederalExciseLastUpdated() {
  return useQuery({
    queryKey: ['federalExciseLastUpdated'],
    queryFn: ({ signal }) => getFederalExciseLastUpdated(signal),
  })
}

export function useUpdateFederalExciseMappings({ handleOnSuccess }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateFederalExciseMappings'],
    mutationFn: (data: UpdateFederalExciseMappings) => updateFederalExciseMappings(data),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      // Covers the grids, the export and the unmapped count.
      queryClient.invalidateQueries({ queryKey: ['federalExcise'] })
      queryClient.removeQueries({ queryKey: ['federalExcise', 'export'] })
      queryClient.invalidateQueries({ queryKey: ['federalExciseLastUpdated'] })
      queryClient.invalidateQueries({ queryKey: ['lastUpdatedFederalExciseLog'] })
      // The price list shows each SKU's resolved excise.
      queryClient.invalidateQueries({ queryKey: ['priceList'] })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('federalExcise.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const fallbackDescription = getFirstZodErrorMessage(
        extractZodIssues(error),
        t,
        'errors.federalExcise.updateFailed',
        'crt',
      )
      // The backend rejects a key its grid does not list as a custom issue carrying the key and category.
      const [ruleIssue] = extractCustomZodIssues(error)
      const message = ruleIssue
        ? getTranslatedCustomIssueDescription(ruleIssue, t, {
            ns: 'crt',
            keyPrefix: 'errors.federalExcise',
            fallbackDescription,
          })
        : fallbackDescription

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
